<script lang="ts">
  import { onMount } from 'svelte';
  import { api, type Health, type Trip, type User } from './lib/api';
  import { adoptTokenFromFragment, clearToken, getToken } from './lib/session';
  import TripList from './lib/TripList.svelte';
  import {
    einladungsTokenAus,
    geteilterTokenAus,
    hilfelink,
    istHilfeweg,
    istVerwaltungsweg,
    VERWALTUNGSWEG,
    mitplanenTokenAus
  } from './lib/wege';
  import Hinweis from './gestaltung/muster/Hinweis.svelte';
  import { DICHTE_ATTRIBUT, DICHTE_VORGABE, dichteVon } from './lib/dichte';
  import {
    SEITEN,
    hashFuer,
    seiteAusHash,
    type Grundlagenseite
  } from './lib/grundlagen/wege';
  // Die eine Seitenleiste (Vertrag NAVIGATION A, 14.9.2026). Sie steht hier
  // und nicht in den drei Flächen: Sie ist für alle drei dieselbe, und drei
  // Kopien wären drei Meinungen darüber, wo man gerade ist.
  import Seitenleiste, { type Reisestand } from './lib/navigation/Seitenleiste.svelte';
  import { type Reiterschluessel } from './lib/navigation/phasen';
  import { NEU_ANKER, alsGelesenMerken, gelesenStand, istUngelesen } from './lib/navigation/neu';
  import { CHANGELOG } from './lib/hilfe/changelog';
  import { GLIEDERUNG } from './lib/hilfe/gliederung';
  import Zeichen from './gestaltung/Zeichen.svelte';
  import { datum as datumDeutsch } from './lib/datum';

  // Der Planer zieht MapLibre nach sich — rund 900 kB, also der Großteil des
  // Bundles. Er wird erst geladen, wenn wirklich eine Reise geöffnet wird;
  // Anmeldeseite und Reiseliste bleiben dadurch klein und schnell.
  const planerLaden = () => import('./lib/Planner.svelte');

  /**
   * Die drei Wege in die Anwendung ohne Anmeldung.
   *
   * Bewusst vor allem anderen geprüft: Wer `/geteilt/<token>` öffnet, soll
   * weder einen Anmeldeschirm sehen noch einen Gesundheitscheck abwarten. Er
   * hat einen Link von einem Freund bekommen und will die Reise sehen. Für
   * `/einladung/<token>` gilt dasselbe aus dem umgekehrten Grund: Wer den
   * Link öffnet, hat noch kein Konto — ein Anmeldeschirm wäre die Antwort auf
   * eine Frage, die er noch gar nicht gestellt hat.
   *
   * `/mitplanen/<token>` steht hier trotz Anmeldepflicht **für den Knopf**:
   * Der Server gibt den Reisenamen ohne Anmeldung heraus, und genau der ist
   * der Grund, sich anzumelden. Wer diese Seite hinter den Anmeldeschirm
   * stellte, verlangte die Zustimmung vor der Auskunft, wozu — und die Seite
   * kümmert sich anschließend selbst darum, ob eine Sitzung gilt.
   *
   * Immer noch kein Router; die Erkennung steht in `lib/wege.ts`, damit die
   * Muster ohne Browser prüfbar sind.
   */
  const geteilterToken = geteilterTokenAus(location.pathname);
  const einladungsToken = einladungsTokenAus(location.pathname);
  const mitplanenToken = mitplanenTokenAus(location.pathname);
  const geteiltLaden = () => import('./lib/GeteilteReise.svelte');
  const einladungLaden = () => import('./lib/Einladung.svelte');
  const mitplanenLaden = () => import('./lib/Mitplanen.svelte');

  /**
   * Der dritte offene Weg: `/hilfe`.
   *
   * Steht bei den anderen beiden und nicht bei `/verwaltung`, weil er sich wie
   * sie verhält: kein Konto, keine Vorprüfung, kein Warten auf `/auth/me`. Wer
   * eingeladen wurde und wissen will, worauf er sich einlässt, soll lesen
   * können, bevor er sich anmeldet — dieselbe Überlegung, aus der die
   * Einladungsseite überhaupt existiert.
   *
   * Angemeldet führt derselbe Weg zu derselben Seite. Eine zweite Fassung „für
   * Angemeldete" wäre eine zweite Anleitung, und eine davon wäre bald falsch.
   */
  const hilfeweg = istHilfeweg(location.pathname);
  const hilfeLaden = () => import('./lib/hilfe/Hilfe.svelte');

  /**
   * Der dritte Weg: `/verwaltung`.
   *
   * Anders als die beiden darüber verlangt er eine Anmeldung — er wird deshalb
   * **nach** dem Gesundheitscheck und dem Kontoabruf ausgewertet und nicht
   * davor. Wer die Adresse ohne Anmeldung öffnet, sieht die Anmeldeseite; das
   * ist dieselbe Antwort wie auf jede andere Adresse dieser Anwendung.
   *
   * Lazy geladen wie der Planer, aus demselben Grund und mit umgekehrter
   * Rechnung: Diese Fläche sieht **ein** Konto, und alle anderen sollen ihre
   * Tabellen, Formulare und die Reibung nie herunterladen müssen.
   */
  const verwaltungsweg = istVerwaltungsweg(location.pathname);
  // Seit dem 14.9.2026 ist die Verwaltung eine eigene Anwendung unter
  // `/verwaltung/` (eigenes Bundle, Nginx liefert es direkt aus). Diesen
  // Zweig erreicht nur noch der alte Pfad ohne Schrägstrich — er leitet um,
  // statt die alte Fläche zu laden.
  if (verwaltungsweg && !location.pathname.endsWith('/')) {
    location.replace(VERWALTUNGSWEG);
  }

  /**
   * Der Bereich „Grundlagen" — der zweite Einstieg, und der erste mit Hash.
   *
   * ## Warum ein Hash und kein Pfad
   *
   * Weil er die offene Reise **nicht** verlieren darf. Die vier offenen Wege
   * darüber sind Pfade und jeder davon eine eigene Seite: Wer sie öffnet,
   * bleibt dort. Die Grundlagen sind das nicht — man kommt aus ihnen zurück
   * in die Reise, in der man gerade saß, und `offen` liegt dafür weiter in
   * diesem Zustand. Ein Pfad verlangte zusätzlich eine nginx-Regel; ein Hash
   * kostet nichts. Die Erkennung steht in `grundlagen/wege.ts`, damit die
   * Muster ohne Browser prüfbar sind (dieselbe Trennung wie bei `wege.ts`).
   *
   * Lazy geladen wie Planer, Verwaltung und Hilfe, und mit derselben
   * Rechnung wie die Verwaltung: Diese Fläche öffnet man selten, und wer sie
   * nie öffnet, soll ihre Formulare nie herunterladen.
   */
  let grundlagenSeite = $state<Grundlagenseite | null>(seiteAusHash(location.hash));
  const grundlagenLaden = () => import('./lib/grundlagen/Grundlagen.svelte');

  /**
   * Hinaus aus den Grundlagen — und **ohne** ein `#` in der Adresszeile.
   *
   * `location.hash = ''` ließe ein nacktes `#` stehen; das sieht nach einem
   * halb geladenen Link aus und wanderte in jede kopierte Adresse mit.
   * `replaceState` räumt es weg, ohne einen zweiten Verlaufseintrag zu
   * erzeugen — zurückzukommen ist ja der Zweck des Knopfs.
   */
  function grundlagenSchliessen() {
    history.replaceState(null, '', location.pathname + location.search);
    grundlagenSeite = null;
  }

  /**
   * Die Anmeldeseite — seit dem Schaufenster eine eigene Fläche.
   *
   * Lazy wie Planer, Verwaltung und Hilfe, hier aber mit der umgekehrten
   * Rechnung: Diese Seite sieht **jeder**, und genau einmal. Wer angemeldet
   * ist, lädt sie nie wieder; wer nicht angemeldet ist, lädt dafür nichts von
   * der Reiseliste. Der Ladezustand dazwischen ist derselbe wie überall.
   */
  const schaufensterLaden = () => import('./lib/Schaufenster.svelte');

  let user = $state<User | null>(null);
  let health = $state<Health | null>(null);
  let error = $state<string | null>(null);
  let busy = $state(true);

  // Die offene Reise. Kein Router: Bei zwei Ansichten wäre eine Routing-
  // Bibliothek mehr Ballast als Nutzen. Wenn M4 Deeplinks auf einzelne Reisen
  // braucht, ist das der Moment dafür.
  let offen = $state<Trip | null>(null);

  /** Ein Konto nur fürs Welterbe-Album: der Schalter an, die Planung ausdrücklich aus. */
  const nurAlbum = $derived(user?.funktionen?.welterbe === true && user?.funktionen?.planung === false);

  onMount(async () => {
    // Auf den vier offenen Seiten gibt es hier nichts zu tun: Die geteilte
    // Reise, die Einladung und die Mitplaner-Seite holen ihre Daten selbst
    // (letztere fragt auch selbst nach der Sitzung), die Hilfe braucht
    // überhaupt keine.
    //
    // **Bis auf das Fragment.** Von den vier ist die Mitplaner-Seite die
    // einzige, auf die der Anmeldeablauf zurückführt — sie schickt
    // `next=/mitplanen/<token>` mit, und der Callback hängt das frische
    // Sitzungstoken als `#session=…` an. Wer es hier nicht abholt, kommt aus
    // dem Apple-Umweg abgemeldet zurück und dreht sich im Kreis. Der Aufruf
    // räumt die Adresszeile zugleich auf, damit das Token nicht im Verlauf
    // und in weitergeleiteten Links steht.
    if (geteilterToken || einladungsToken || mitplanenToken || hilfeweg) {
      if (mitplanenToken) {
        try {
          adoptTokenFromFragment();
        } catch {
          // Ein `error=` im Fragment (abgelehnte Anmeldung). Die
          // Mitplaner-Seite stellt gleich wieder den Anmeldeknopf hin — das
          // ist die Antwort, die hier zählt, und sie steht dort, wo der
          // Nutzer hinsieht.
        }
      }
      busy = false;
      return;
    }
    try {
      adoptTokenFromFragment();
    } catch (e) {
      error = (e as Error).message;
    }

    // Health geht immer — er ist der schnellste Beweis, dass Backend und DB stehen.
    try {
      health = await api.health();
    } catch {
      error ??= 'Backend nicht erreichbar.';
    }

    if (getToken()) {
      try {
        user = (await api.me()).user;
      } catch {
        // Token war ungültig; api.ts hat es schon weggeräumt.
      }
    }
    busy = false;
  });

  async function logout() {
    await api.logout().catch(() => {});
    clearToken();
    user = null;
    offen = null;
  }

  /**
   * Die Anzeigedichte am Wurzelelement nachziehen.
   *
   * Sie steht als `data-dichte="einfach"` schon im ausgelieferten HTML; hier
   * wird sie auf den Kontostand gehoben, sobald `GET /auth/me` geantwortet
   * hat. Am `<html>` und nicht an einem Bauteil, weil `token/anwendung.css`
   * dort hört — und weil der Planer in einem anderen Zweig dieses Baums lebt
   * als die Reiseliste.
   *
   * Nach dem Abmelden fällt sie auf die Vorgabe zurück: Der nächste Mensch an
   * diesem Rechner ist womöglich ein anderer.
   */
  $effect(() => {
    document.documentElement.setAttribute(
      DICHTE_ATTRIBUT,
      user ? dichteVon(user) : DICHTE_VORGABE
    );
  });

  /**
   * Der Schalter „Expertenansicht".
   *
   * Erst schreiben, dann übernehmen — und die Antwort des Servers gilt, nicht
   * die eigene Annahme. Ein Schalter, der umspringt und dessen Wert beim
   * nächsten Laden wieder anders ist, ist schlimmer als einer, der kurz
   * wartet. Schlägt der Aufruf fehl, bleibt der alte Stand stehen und der
   * Satz daneben sagt, warum.
   */
  let dichteFehler = $state<string | null>(null);

  async function expertenansichtUmschalten(an: boolean) {
    dichteFehler = null;
    try {
      user = (await api.patchMe({ expertenansicht: an })).user;
    } catch (e) {
      dichteFehler =
        e instanceof Error ? e.message : 'Die Einstellung ließ sich nicht speichern.';
    }
  }

  /* ══ Die Standard-Packliste ist umgezogen ═══════════════════════════
   *
   * Sie stand hier in einem `<details>` unter dem Dichteschalter: dreißig
   * Zeilen Editor in einer Fläche, die man öffnet, um sich abzumelden. Das
   * war die kleinste ehrliche Lösung, solange es keinen Ort für globale
   * Einstellungen gab — seit dem 12.9.2026 gibt es einen, und sie hat dort
   * eine ganze Seite (`grundlagen/PacklisteSeite.svelte`).
   *
   * Mit ihr gingen der Schalter „Expertenansicht" (als Fläche; der Griff
   * `expertenansichtUmschalten` bleibt hier, weil der Nutzer hier liegt),
   * der Verweis in die Verwaltung und „Abmelden" auf die Seite „Konto und
   * Zugang". Der Verweis, der danach hier stand, ist mit der Seitenleiste
   * weggefallen: Sie führt von jeder Fläche dorthin.
   */

  /* ══ Die Navigation ══════════════════════════════════════════════════
   *
   * Drei Zustände, die alle drei angemeldeten Flächen teilen und die deshalb
   * hier liegen und nicht in einer von ihnen.
   */

  /**
   * Der offene Reiter der offenen Reise.
   *
   * Er steht hier, weil die Leiste ihn wechselt und der Planer ihn anzeigt —
   * und die beiden stehen seit diesem Umbau nebeneinander statt ineinander.
   * Gesetzt wird er beim Öffnen vom Planer selbst (er kennt Phase und
   * Merkung); hier steht nur der Anfangswert, bis es so weit ist.
   */
  let ansicht = $state<Reiterschluessel>('stationen');

  /**
   * Was die Leiste über die offene Reise weiß — vom Planer gemeldet.
   *
   * `null`, solange keine Reise offen ist. Der Planer meldet ihn bei jeder
   * Änderung (`onNavigationsstand`); hier wird er nur weitergereicht. Beim
   * Schließen der Reise wird er ausdrücklich geleert, sonst stünde der Name
   * der letzten Reise in einer Leiste ohne Reise.
   */
  let navstand = $state<Reisestand | null>(null);

  /** Auf dem Telefon: Ist die Schublade aufgezogen? */
  let schubladeOffen = $state(false);

  /* ── Was ist neu ──────────────────────────────────────────────────
   *
   * Der jüngste Changelog-Eintrag gegen den gemerkten Stand im Browser. Die
   * Regel steht in `navigation/neu.ts`, der Speicherzugriff hier — dieselbe
   * Trennung wie überall im Haus.
   *
   * Der Changelog wandert damit ins Hauptbündel. Das ist der Preis, und er
   * ist mit Bedacht bezahlt: Die Alternative wäre ein zweites Feld mit dem
   * jüngsten Datum, das jemand beim nächsten Eintrag zu ändern vergisst —
   * und dann steht der Punkt nie wieder da.
   */
  const NEUESTES = CHANGELOG[0]?.datum ?? null;
  let neuGelesen = $state<string | null>(gelesenStand(globalThis.localStorage));
  const neuUngelesen = $derived(istUngelesen(NEUESTES, neuGelesen));

  /** Die drei jüngsten Neuerungen — die rechte Spalte der Reiseliste. */
  const NEUERUNGEN = CHANGELOG.slice(0, 3);

  /**
   * Drei Einstiege ins Handbuch — die dritte Karte der rechten Spalte.
   *
   * Aus der Gliederung und nicht abgeschrieben: Ein Titel, der hier noch
   * „Unterwegs mit der App" heißt und dort längst anders, wäre ein Link, der
   * etwas anderes verspricht, als er hält.
   */
  const HANDBUCH_EINSTIEGE = ['erste-reise', 'so-plant-es-sich', 'unterwegs']
    .map((id) => GLIEDERUNG.find((a) => a.id === id))
    .filter((a): a is (typeof GLIEDERUNG)[number] => !!a);

  /**
   * Was unter einer Grundlagen-Kachel steht.
   *
   * Ein **Stand**, wo einer ohne zusätzlichen Abruf dasteht, sonst der
   * Halbsatz aus `grundlagen/wege.ts`, der sagt, was auf der Seite steht. Und
   * kein zweiter Abruf dafür: Die Reiseliste soll nicht langsamer werden,
   * damit vier Kacheln eine Zahl tragen. Heute hat nur die Packliste einen
   * Stand in `/auth/me` — Fahrzeug, Ladekarten und Reiseprofile liegen hinter
   * eigenen Abrufen, und ein Halbsatz ist ehrlicher als eine erfundene Zahl.
   */
  function kachelzeile(s: (typeof SEITEN)[number]): string {
    if (s.id === 'packliste' && user?.packliste?.posten?.length) {
      // „Posten" ist im Deutschen in beiden Zahlen gleich — keine Weiche nötig.
      return `${user.packliste.posten.length} Posten in der Vorlage`;
    }
    return s.zeile;
  }

  /**
   * Die Hilfe in einem neuen Tab — für „Was ist neu" und „Handbuch".
   *
   * Derselbe Weg wie bei Cmd-K im Planer und aus demselben Grund: Hier liegen
   * womöglich halb getippte Felder und eine offene Reise, und die Hilfe ist
   * eine eigene Seite ohne Rückweg dorthin. `noopener`, weil die geöffnete
   * Seite an diesem Fenster nichts zu suchen hat.
   */
  function hilfeOeffnen(anker?: string) {
    // `hilfelink` und kein zusammengebauter Hash: Seit dem 15.9.2026 ist die
    // Hilfe ein Handbuch mit einem Kapitel je Pfad (`/hilfe/kosten`), und
    // welches Kapitel eine Kennung meint, weiß die Gliederung.
    window.open(hilfelink(anker), '_blank', 'noopener');
  }

  /** „Was ist neu" öffnen — und den Stand merken, damit der Punkt ausgeht. */
  function neuOeffnen() {
    alsGelesenMerken(globalThis.localStorage, NEUESTES);
    neuGelesen = NEUESTES;
    hilfeOeffnen(NEU_ANKER);
  }

  /** Zurück in die Reiseliste — die Reise wird geschlossen. */
  function reisenOeffnen() {
    offen = null;
    navstand = null;
    grundlagenSchliessen();
  }

  /**
   * In die Grundlagen, auf diese Seite.
   *
   * Über den Hash und nicht über den Zustand: Der Weg gehört der Adresszeile
   * (siehe `grundlagen/wege.ts`), und zwei Stellen, die daran schreiben, sind
   * zwei Meinungen darüber, wo man ist. Die offene Reise bleibt dabei liegen.
   */
  function grundlagenOeffnen(seite: Grundlagenseite) {
    location.hash = hashFuer(seite);
  }

  /** Welche der drei Flächen offen ist — die Leiste zeigt es an. */
  const bereich = $derived<'reisen' | 'grundlagen' | 'planer'>(
    grundlagenSeite ? 'grundlagen' : offen ? 'planer' : 'reisen'
  );

  /**
   * Was auf dem Telefon im schmalen Kopfbalken steht.
   *
   * Der Balken ist der Griff an die Schublade und zugleich die Antwort auf
   * „wo bin ich" — ohne ihn wäre eine zugeschobene Leiste eine Anwendung ohne
   * Ortsangabe.
   */
  const telefontitel = $derived(
    bereich === 'grundlagen'
      ? 'Grundlagen'
      : bereich === 'planer'
        ? navstand?.name || 'Reise'
        : 'Meine Reisen'
  );
</script>

<!-- Die Vor- und Zurück-Taste des Browsers wechselt die Grundlagen-Seite, und
     `onSeite` geht denselben Weg (es schreibt nur den Hash). Ein Zustand, der
     am Hash hängt, hat damit genau eine Quelle. -->
<svelte:window onhashchange={() => (grundlagenSeite = seiteAusHash(location.hash))} />

{#if geteilterToken}
  {#await geteiltLaden()}
    <p class="laden">lädt …</p>
  {:then modul}
    <modul.default token={geteilterToken} />
  {:catch}
    <p class="laden">Diese Seite lässt sich gerade nicht laden.</p>
  {/await}
{:else if einladungsToken}
  {#await einladungLaden()}
    <p class="laden">lädt …</p>
  {:then modul}
    <modul.default token={einladungsToken} />
  {:catch}
    <p class="laden">Diese Seite lässt sich gerade nicht laden.</p>
  {/await}
{:else if mitplanenToken}
  <!-- Ohne `user` in der Bedingung, obwohl Mitplanen ein Konto verlangt: Die
       Seite entscheidet selbst, ob sie den Knopf „Mitplanen" oder den
       Anmeldeknopf zeigt — und sie muss die Reise benennen können, bevor
       jemand sich für sie anmeldet. -->
  {#await mitplanenLaden()}
    <p class="laden">lädt …</p>
  {:then modul}
    <modul.default token={mitplanenToken} />
  {:catch}
    <p class="laden">Diese Seite lässt sich gerade nicht laden.</p>
  {/await}
{:else if hilfeweg}
  <!-- Vor der Anmeldeprüfung und ohne `user` in der Bedingung: Die Hilfe ist
       für den gedacht, der noch kein Konto hat, und sie sieht für alle gleich
       aus. -->
  {#await hilfeLaden()}
    <p class="laden">lädt …</p>
  {:then modul}
    <modul.default />
  {:catch}
    <p class="laden">Diese Seite lässt sich gerade nicht laden.</p>
  {/await}
{:else if verwaltungsweg}
  <!-- Die Verwaltung wohnt unter `/verwaltung/` in eigener Anwendung; der
       Kopf dieser Datei leitet dorthin um. -->
  <p class="laden">Die Verwaltung öffnet sich unter <a href={VERWALTUNGSWEG}>/verwaltung/</a> …</p>
{:else if busy}
  <p class="laden">lädt …</p>
{:else if user && nurAlbum}
  <!-- Ein Konto, das nur das Welterbe-Album hat (21.10.2026, Vertrag
       WELTERBE-1 § 3): `welterbe` an, `planung` aus. Für dieses Konto ist
       die Reiseliste eine leere Fläche mit Knöpfen, die ins Leere führen —
       also steht hier der Weg, und sonst nichts. Dass `planung` am Server
       nicht durchgesetzt wird, ändert daran nichts: Das hier entscheidet, was
       die Fläche zeigt, nicht, was jemand darf. -->
  <div class="albumzugang">
    <h1>Dein Zugang gilt für das Welterbe-Album</h1>
    <p>
      Dieses Konto ist für das Album eingerichtet, nicht für die Reiseplanung.
      Das Album hat eine eigene Adresse, angemeldet bist du dort schon.
    </p>
    <p><a class="albumknopf" href="/welterbe/">Zum Welterbe-Album</a></p>
    <p class="albumfuss"><a href="/hilfe/welterbe">Was das Album ist</a></p>
  </div>
{:else if user}
  <!--
    ══ Die angemeldete Anwendung ═══════════════════════════════════════

    Eine Leiste, drei Flächen. Bis zum 14.9.2026 brachte jede Fläche ihre
    eigene Navigation mit (Verweiszeile, Bereichsliste, elf Reiter), und keine
    davon sagte, was es außer ihr noch gibt. Jetzt steht die Leiste **einmal**
    hier, und die Flächen stehen daneben; welche gerade dran ist, entscheidet
    dieselbe Weiche wie vorher — der Hash schlägt die offene Reise, die offene
    Reise schlägt die Liste.

    Die Verwaltung bleibt bewusst außerhalb dieses Rahmens (siehe ihren Zweig
    oben): Sie bringt ihre eigene Kopfzeile mit und wandert ohnehin auf einen
    eigenen Pfad.
  -->
  <div class="rahmen">
    <Seitenleiste
      {bereich}
      grundlagenseite={grundlagenSeite}
      reise={offen ? navstand : null}
      {ansicht}
      onReiter={(r) => {
        ansicht = r;
        // Aus den Grundlagen heraus auf einen Reiter zu tippen heißt: zurück
        // in die Reise. Ohne diese Zeile wechselte der Reiter hinter einer
        // Fläche, die gar nicht die Reise ist.
        if (grundlagenSeite) grundlagenSchliessen();
      }}
      onReisen={reisenOeffnen}
      onGrundlagen={grundlagenOeffnen}
      {neuUngelesen}
      onNeu={neuOeffnen}
      onHandbuch={() => hilfeOeffnen()}
      offen={schubladeOffen}
      onSchliessen={() => (schubladeOffen = false)}
      welterbe={user.funktionen?.welterbe === true}
    />

    <div class="flaeche">
      <!-- Der Kopfbalken für das Telefon: der Griff an die Schublade und die
           Antwort auf „wo bin ich". Über 820 px steht er nicht da — dort ist
           die Leiste sichtbar, und ein zweiter Ortsname wäre einer zu viel. -->
      <div class="telefonkopf">
        <button
          type="button"
          class="menueknopf"
          aria-label="Menü öffnen"
          aria-expanded={schubladeOffen}
          onclick={() => (schubladeOffen = true)}
        >
          <Zeichen name="menue" groesse={22} />
          {#if neuUngelesen}
            <span class="punktmarke"><span class="nurlesbar">Neues ungelesen</span></span>
          {/if}
        </button>
        <span class="wo">{telefontitel}</span>
      </div>

      <div class="inhalt">
        {#if grundlagenSeite}
          <!--
            Die Grundlagen. Sie stehen **vor** dem Planer in dieser Kette,
            obwohl eine Reise offen sein kann: Der Hash ist die jüngere Aussage
            darüber, was jemand gerade ansieht, und die offene Reise bleibt in
            `offen` liegen. Ihren Zurück-Knopf hat sie mit diesem Umbau
            verloren — der Weg zurück steht in der Leiste, und zwar auf jeder
            Fläche derselbe.
          -->
          {#await grundlagenLaden()}
            <p class="laden">lädt …</p>
          {:then modul}
            <modul.default
              nutzer={user}
              seite={grundlagenSeite}
              onSeite={(s) => (location.hash = hashFuer(s))}
              onUser={(frisch) => (user = frisch)}
              onAbmelden={logout}
            />
          {:catch}
            <p class="laden error">Die Grundlagen konnten nicht geladen werden. Seite neu laden.</p>
          {/await}
        {:else if offen}
          <!-- Der Planer füllt die Fläche neben der Leiste und bringt seine
               eigene Kopfzeile mit. -->
          {#await planerLaden()}
            <p class="laden">Karte lädt …</p>
          {:then modul}
            <!-- `onDichteUmschalten`: Der Schalter bleibt hier — hier liegt der
                 Nutzer, hier wird geschrieben, hier hängt das Attribut am
                 Wurzelelement. Der Planer bekommt nur den Griff, damit die
                 Befehlssuche (Cmd-K) ihn anbieten kann, ohne die Reise zu
                 verlassen. Ohne Argument, weil der aktuelle Stand hier steht
                 und nicht dort.

                 `bind:ansicht`: Der offene Reiter gehört seit diesem Umbau
                 beiden — der Leiste, die ihn wechselt, und dem Planer, der ihn
                 zeigt und selbst umschaltet (Befehlssuche, Rückgängig). -->
            <modul.default
              trip={offen}
              onBack={reisenOeffnen}
              bind:ansicht
              onNavigationsstand={(stand) => (navstand = stand)}
              onDichteUmschalten={() => expertenansichtUmschalten(dichteVon(user) !== 'experte')}
            />
          {:catch}
            <p class="laden error">Der Planer konnte nicht geladen werden. Seite neu laden.</p>
          {/await}
        {:else}
          <!-- Die Reiseliste, und rechts daneben, was sonst niemand zu sehen
               bekam: die jüngsten Neuerungen, die Grundlagen und das Handbuch.
               Die Verweiszeile unter der Liste ist dafür weggefallen — sie
               stand an der Stelle, an der man aufhört zu lesen. -->
          <main class="reiseliste">
            <!--
              Keine eigene Überschrift mehr: „Meine Reisen" stand hier als `h1`
              und eine Zeile tiefer noch einmal im Panelkopf der Liste, samt
              Anzahl. Zweimal dasselbe Wort untereinander liest sich als
              Versehen — und die Liste bringt ihren Kopf ohnehin mit.
            -->
            {#if error}
              <p class="error" role="alert">{error}</p>
            {/if}

            {#if dichteFehler}
              <!-- Der Schalter selbst steht auf der Kontoseite; umgelegt wird
                   er von hier aus nur noch über Cmd-K im Planer. Schlägt das
                   fehl, gehört die Meldung an die Stelle, an der der Nutzer
                   liegt — und das ist diese. -->
              <Hinweis art="leise">
                {dichteFehler} Der Schalter steht weiter auf dem Stand, den der
                Server kennt.
              </Hinweis>
            {/if}

            <div class="listenraster">
              <div class="listenspalte">
                <!-- `nutzer` seit dem 12.9.2026: Der Anlegen-Dialog fragt nach
                     dem Reiseprofil und bietet „Zuhause als Start und Ziel" an
                     — beides steht am Konto, und hier liegt es. -->
                <TripList nutzer={user} onOpen={(t) => (offen = t)} />
              </div>

              <aside class="nebenspalte" aria-label="Neuerungen, Grundlagen und Handbuch">
                <section class="karte">
                  <h2>Was ist neu</h2>
                  <ul class="neuerungen">
                    {#each NEUERUNGEN as e (e.titel)}
                      <li>
                        <span class="titel">{e.titel}</span>
                        <span class="dim">{datumDeutsch(e.datum)}</span>
                      </li>
                    {/each}
                  </ul>
                  <button type="button" class="mehr" onclick={neuOeffnen}>
                    Alle Änderungen
                  </button>
                </section>

                <section class="karte">
                  <h2>Grundlagen</h2>
                  <!-- Der Name des angemeldeten Kontos stand bis zum 14.9.2026
                       unter der Liste. Er steht jetzt hier, wo auch der Weg zum
                       Konto anfängt — und nicht mehr allein in einer Zeile,
                       die sonst nichts mehr enthielt. -->
                  <p class="wer dim">{user.display_name ?? user.email ?? `Nutzer #${user.id}`}</p>
                  <div class="kacheln">
                    <!-- Vier von fünf Seiten: „Konto und Zugang" fehlt hier mit
                         Absicht, sie steht als eigener Punkt in der Leiste.
                         Unter jeder Kachel der Halbsatz, der ohnehin schon
                         sagt, was auf der Seite steht (`grundlagen/wege.ts`) —
                         ein zweiter Satz mit demselben Inhalt wäre einer, der
                         eines Tages etwas anderes behauptet. -->
                    {#each SEITEN.filter((x) => x.id !== 'konto') as s (s.id)}
                      <button type="button" class="kachel" onclick={() => grundlagenOeffnen(s.id)}>
                        <span class="titel">{s.titel}</span>
                        <span class="dim">{kachelzeile(s)}</span>
                      </button>
                    {/each}
                  </div>
                </section>

                <section class="karte">
                  <h2>Handbuch</h2>
                  <ul class="einstiege">
                    <!-- Nur die Titel und nicht die Halbsätze der Gliederung:
                         Drei Einstiege sollen nach drei Türen aussehen, nicht
                         nach einem Inhaltsverzeichnis. Was drinsteht, sagt die
                         Hilfe selbst — sie ist einen Klick entfernt. -->
                    {#each HANDBUCH_EINSTIEGE as a (a.id)}
                      <li>
                        <button type="button" class="mehr" onclick={() => hilfeOeffnen(a.id)}>
                          {a.titel}
                        </button>
                      </li>
                    {/each}
                  </ul>
                </section>
              </aside>
            </div>

            <!--
              Der Zustandsbericht meldet sich nur noch, wenn es etwas zu melden
              gibt. Für den Betrieb ist die Zeile wertlos, solange alles läuft;
              interessant wird sie genau dann, wenn etwas klemmt. Die Zahlen
              stehen weiterhin unter `/api/health`.
            -->
            {#if health && health.status !== 'ok'}
              <footer role="status">
                <span class="warnung">
                  Das Backend meldet sich mit „{health.status}“ statt „ok“.
                  Gespeichert wird trotzdem — aber Routen und Vorschläge können
                  ausbleiben.
                </span>
              </footer>
            {/if}
          </main>
        {/if}
      </div>
    </div>
  </div>
{:else}
  <!--
    Nicht angemeldet: das Schaufenster.

    Health-Zustand und Startfehler gehen als Eigenschaften hinein — dort hängt
    die Weiche daran, die früher hier stand („Server nicht erreichbar",
    „Anmeldung nicht eingerichtet", sonst der Apple-Knopf). Sie ist Wort für
    Wort dieselbe geblieben; nur der Rahmen darum ist neu.
  -->
  {#await schaufensterLaden()}
    <p class="laden">lädt …</p>
  {:then modul}
    <modul.default {health} fehler={error} />
  {:catch}
    <p class="laden">Diese Seite lässt sich gerade nicht laden.</p>
  {/await}
{/if}

<style>
  /* ══ Der Rahmen: Leiste links, Fläche rechts ═══════════════════════
     Eine Rasterzeile über die volle Fensterhöhe. Der Planer war bis zum
     14.9.2026 selbst 100 dvh hoch und füllte das Fenster; jetzt füllt er
     seine Spalte, und die Höhe steht hier. */
  .rahmen {
    display: grid;
    grid-template-columns: 260px minmax(0, 1fr);
    height: 100dvh;
  }

  /* Die Fläche selbst: darüber der Kopfbalken fürs Telefon (über 820 px
     null hoch, weil er dort nicht gezeichnet wird), darunter der Inhalt. */
  .flaeche {
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    min-width: 0;
    min-height: 0;
  }
  .inhalt {
    min-height: 0;
    min-width: 0;
    overflow-y: auto;
  }
  /* Der Planer bringt seine eigene Höhe mit und darf nicht zweimal scrollen —
     seine Kopfleiste soll stehen bleiben, während die Panels rollen. */
  .inhalt:has(> :global(.planer)) {
    overflow-y: hidden;
  }

  .telefonkopf {
    display: none;
  }

  /* ── Die Reiseliste ───────────────────────────────────────────────
     Links die Liste auf Lesebreite, rechts eine Spalte mit dem, was sonst
     niemand zu sehen bekam. 46 rem für die Liste: Sie ist eine Spalte und
     keine Kachelgalerie. */
  main {
    max-width: 76rem;
    margin: 0 auto;
    padding: var(--gruppe-weit) var(--raum-8) var(--raum-10);
    display: flex;
    flex-direction: column;
    gap: var(--gruppe);
  }

  .listenraster {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 22rem;
    gap: var(--gruppe-weit);
    align-items: start;
  }
  .listenspalte {
    min-width: 0;
    max-width: 46rem;
  }

  /* Unter 1100 px hat die zweite Spalte keinen Platz mehr. Sie fällt dann
     **unter** die Liste und verschwindet nicht: Auf dem Telefon ist sie der
     einzige Weg zu den Neuerungen, den niemand suchen muss. */
  @media (max-width: 1100px) {
    .listenraster {
      grid-template-columns: minmax(0, 1fr);
      gap: var(--gruppe);
    }
    .listenspalte {
      max-width: none;
    }
  }

  .nebenspalte {
    display: flex;
    flex-direction: column;
    gap: var(--gruppe-eng);
    min-width: 0;
  }

  .karte {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: var(--raum-7);
    display: flex;
    flex-direction: column;
    gap: var(--raum-5);
  }
  .karte h2 {
    margin: 0;
    font-size: var(--fs-panel);
    font-weight: var(--gw-halb);
  }

  .neuerungen,
  .einstiege {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--raum-5);
  }
  .neuerungen li,
  .einstiege li {
    display: grid;
    gap: 1px;
  }
  .neuerungen .titel {
    font-size: var(--fs-text);
    font-weight: var(--gw-halb);
    text-wrap: pretty;
  }
  .neuerungen .dim {
    font-size: var(--fs-fein);
    text-wrap: pretty;
  }

  /* Ein Knopf, der aussieht wie ein Link: Das Ziel ist ein neues Fenster,
     und das öffnet hier die Anwendung und nicht der Browser (siehe
     `hilfeOeffnen`). Ein `a` mit `target` wäre dasselbe zweimal gesagt. */
  .mehr {
    justify-self: start;
    border: 0;
    background: none;
    padding: 0;
    font: inherit;
    font-size: var(--fs-text);
    font-weight: var(--gw-halb);
    color: var(--accent);
    text-align: left;
  }
  .mehr:hover {
    color: var(--accent-stark);
  }

  .kacheln {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--raum-4);
  }
  .kachel {
    display: grid;
    gap: 1px;
    text-align: left;
    border: 0;
    background: var(--surface-2);
    border-radius: var(--radius-klein);
    padding: var(--raum-5);
    font: inherit;
    font-size: var(--fs-text);
    min-height: var(--tippziel);
    color: var(--text);
  }
  .kachel:hover {
    background: var(--accent-weich);
  }
  .kachel .titel {
    font-weight: var(--gw-halb);
  }
  /* Der Halbsatz ist die Auskunft, nicht die Überschrift — er darf umbrechen
     und bleibt klein. */
  .kachel .dim {
    font-size: var(--fs-mikro);
    line-height: var(--zh-eng);
    text-wrap: pretty;
  }

  .dim {
    color: var(--text-dim);
  }

  .error {
    margin: 0;
    color: var(--danger);
  }

  .wer {
    margin: 0;
    font-size: var(--fs-fein);
  }

  /* Die Fläche für ein Konto, das nur das Welterbe-Album hat (21.10.2026). */
  .albumzugang {
    max-width: 30rem;
    margin: 12vh auto 0;
    padding: var(--raum-10);
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--schatten);
  }
  .albumzugang h1 {
    font-size: var(--fs-schau);
    margin: 0 0 var(--raum-6);
  }
  .albumzugang p {
    color: var(--text-dim);
    margin: 0 0 var(--raum-6);
  }
  .albumknopf {
    display: inline-block;
    background: var(--accent);
    color: var(--accent-text);
    text-decoration: none;
    padding: var(--raum-5) var(--raum-8);
    border-radius: var(--radius);
    font-weight: var(--gw-halb);
  }
  .albumknopf:hover {
    background: var(--accent-stark);
    color: var(--accent-text);
  }
  .albumfuss {
    font-size: var(--fs-klein);
  }

  .laden {
    display: grid;
    place-items: center;
    height: 100%;
    min-height: 40dvh;
    margin: 0;
    color: var(--text-dim);
  }

  /* ── Das Telefon (unter 820 px) ────────────────────────────────────
     Die Leiste wird zur Schublade (die Regeln dafür stehen bei ihr), und an
     ihrer Stelle steht ein schmaler Balken mit dem Menüknopf und der Antwort
     auf „wo bin ich". Dieselbe Grenze wie im Planer — eine zweite Zahl wäre
     eine zweite Meinung darüber, wann ein Fenster schmal ist. */
  @media (max-width: 820px) {
    .rahmen {
      grid-template-columns: minmax(0, 1fr);
    }
    .telefonkopf {
      display: flex;
      align-items: center;
      gap: var(--raum-3);
      padding: 0 var(--raum-4);
      background: var(--surface);
      border-bottom: 1px solid var(--border);
    }
    .menueknopf {
      position: relative;
      width: var(--tippziel);
      height: var(--tippziel);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 0;
      background: none;
      color: var(--text);
      padding: 0;
    }
    .wo {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: var(--fs-panel);
      font-weight: var(--gw-halb);
    }
    main {
      padding: var(--gruppe) var(--raum-6) var(--raum-10);
    }
  }

  /* Der rote Punkt am Menüknopf — dieselbe Marke wie in der Leiste, und aus
     demselben Grund mit Text darin: Eine Farbe allein sagt einem Vorlesegerät
     nichts. */
  .punktmarke {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: var(--danger);
  }
  .nurlesbar {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  footer {
    display: flex;
    flex-wrap: wrap;
    gap: var(--raum-3) var(--raum-7);
    font-size: var(--fs-text);
    border-top: 1px solid var(--border);
    padding-top: var(--raum-6);
  }

  .warnung {
    color: var(--zustand-pruefen);
    background: var(--zustand-pruefen-flaeche);
    padding: var(--raum-3) var(--raum-5);
    border-radius: var(--radius-klein);
  }

</style>
